import { IconCheck, IconPencil } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import NSIcon, { NSIconSize } from 'bricks/NSIcon/NSIcon';
import NSInput from 'bricks/NSInput/NSInput';
import handleIconKeyDown from 'helpers/handleIconKeyDown';
import './NSEditableText.scss';
import NSButton from 'bricks/NSButton/NSButton';

export interface INSEditableTextProps {
    value: string;
    onSave: (value: string) => void;
    name?: string;
    textClassName?: string;
    dataTestIdPrefix?: string;
    iconSize?: NSIconSize;
    /** When false, the pencil is omitted so a parent can place it as a sibling. */
    showEditIcon?: boolean;
    isEditing?: boolean;
    onEditingChange?: (isEditing: boolean) => void;
}

const NSEditableText = ({
    value,
    onSave,
    name = 'ns-editable-text',
    textClassName = '',
    dataTestIdPrefix = 'ns-editable-text',
    iconSize = 'xs',
    showEditIcon = true,
    isEditing: isEditingProp,
    onEditingChange,
}: INSEditableTextProps) => {
    const isControlled = isEditingProp !== undefined;
    const [uncontrolledEditing, setUncontrolledEditing] = useState(false);
    const isEditing = isControlled ? isEditingProp : uncontrolledEditing;
    const [draftValue, setDraftValue] = useState(value);

    const setIsEditing = (next: boolean) => {
        if (!isControlled) {
            setUncontrolledEditing(next);
        }
        onEditingChange?.(next);
    };

    useEffect(() => {
        setDraftValue(value);
    }, [value]);

    const startEditing = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.stopPropagation();
        setDraftValue(value);
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setDraftValue(value);
        setIsEditing(false);
    };

    const saveValue = () => {
        const trimmedValue = draftValue.trim();
        if (trimmedValue && trimmedValue !== value) {
            onSave(trimmedValue);
        }
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            saveValue();
        } else if (e.key === 'Escape') {
            cancelEditing();
        }
    };

    if (!isEditing) {
        return (
            <span className="d-inline-flex align-items-center">
                <span className={textClassName} data-testid={`${dataTestIdPrefix}-value`}>{value}</span>
                {showEditIcon && (
                    <NSIcon
                        icon={IconPencil}
                        size={iconSize}
                        className="flex-shrink-0 cursor--pointer ml-2"
                        onClick={startEditing}
                        onKeyDown={handleIconKeyDown(startEditing)}
                        aria-label="Edit"
                        aria-hidden={false}
                        focusable
                        data-testid={`${dataTestIdPrefix}-edit`}
                    />
                )}
            </span>
        );
    }

    return (
        <span
            className="d-inline-flex align-items-center"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
            role="presentation"
        >
            <NSInput
                name={name}
                type="text"
                value={draftValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraftValue(e.target.value)}
                onKeyDown={handleKeyDown}
                inputGroupClassName="NSEditableText__input-group"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                selectOnFocus
                hideCancelIcon
                dataTestId={`${dataTestIdPrefix}-input`}
            />
            <NSButton
                icon={IconCheck}
                size={iconSize as 'xs' | 'sm' | 'md' | 'lg'}
                className="text-muted my-auto ml-1"
                callback={saveValue}
                ariaLabel="Save"
                color="light"
                data-testid={`${dataTestIdPrefix}-save`}
            />
        </span>
    );
};

export default NSEditableText;
